import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';

import { LabelInput, LabelRequiredInput, LabelError } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';

import FieldType from 'shared/helpers/fieldType';
import { CustomFieldProps } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';
import { cleanSpaceString } from 'shared/utils';

const GroupForm = ({ defaultValue, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const customField = useSelector((state) => state.customField);
  const [groupFields, setGroupFields] = useState([]);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: defaultValue
      ? {
          groupTitle: defaultValue.name,
          groupFields: customField.customFields.filter(
            (currentField) => currentField.parentId === defaultValue.id
          ),
        }
      : undefined,
  });

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    let newCustomFields = [];
    customField?.customFields
      .filter((item) => item.type !== FieldType.GROUP && item.id)
      .forEach((field) => {
        newCustomFields = [
          ...newCustomFields,
          { name: getNameSelectedLanguage(field.i18List), id: field.id },
        ];
      });
    setGroupFields(newCustomFields);
  }, []);

  useEffect(() => {
    if (defaultValue) {
      setValue('groupTitle', getNameSelectedLanguage(defaultValue.i18List));
    }
  }, [defaultValue]);

  return (
    <Row className="row-content w-100">
      <Col sm={12}>
        <form className="form form-field" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('custom_field_group_form_title_label')}
                  color={colors.black}
                  error={errors.groupTitle !== undefined}
                />
                <Controller
                  name="groupTitle"
                  control={control}
                  defaultValue=""
                  rules={{ required: { value: true, message: t('general_required_field') } }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={(e) => {
                        const newEvent = cleanSpaceString(e);
                        field.onChange(newEvent);
                      }}
                      onBlur={field.onBlur}
                      error={errors.groupTitle !== undefined}
                    />
                  )}
                />
                {errors.groupTitle && <LabelError label={errors.groupTitle.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('custom_field_group_form_fields_label')}
                  color={colors.black}
                  error={errors.groupFields !== undefined}
                />
                <Controller
                  name="groupFields"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <MultipleSelectSearch
                      dataSource={groupFields}
                      fields={{ text: 'name', value: 'id' }}
                      defaultValue={field.value.length ? field.value : undefined}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.groupFields !== undefined}
                    />
                  )}
                />
                {errors.groupFields && <LabelError label={errors.groupFields.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="text-uppercase mr-4"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCancel}
              />
              <Button
                className="text-uppercase"
                type="submit"
                color="primary"
                label={t(defaultValue ? 'general_button_update' : 'general_add')}
              />
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

GroupForm.defaultProps = {
  defaultValue: undefined,
};

GroupForm.propTypes = {
  defaultValue: CustomFieldProps,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default GroupForm;
